import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { BraavosPlanPrices } from '~/features/plans/models/braavos-plan-prices';
import { SanitizedPromotion } from '~/features/promotions/models/promotions';

export type ExperimentPlan = {
  couponApplied?: boolean;
  couponPercentOff?: number | null;
  displayPrice?: string;
  displayPriceUnits: string;
  fullDisplayPrice?: string;
  label: string;
  savingsBlurb?: string | null;
  strikeThroughDisplayPrice?: string | null;
  value: BillingFrequency;
};

export function remapDefaultPlan(defaultPlan: BillingFrequency | undefined, isAnnual: boolean): BillingFrequency {
  if (defaultPlan === billingFrequencySchema.enum['4month'] && isAnnual) {
    return billingFrequencySchema.enum.annually;
  }
  return defaultPlan ?? billingFrequencySchema.enum.monthly;
}

export function getSubscriptionPlans({
  isAnnual,
  plans,
  semanticType,
}: {
  isAnnual: boolean;
  plans?: BraavosPlanPrices;
  semanticType: string;
}): ExperimentPlan[] {
  if (semanticType === 'Course') return [];

  const bundleFrequency = isAnnual ? billingFrequencySchema.enum.annually : billingFrequencySchema.enum['4month'];
  const bundle = plans?.prices[bundleFrequency]?.individual;
  const monthly = plans?.prices.monthly?.individual;

  return [
    {
      ...bundle,
      displayPrice: bundle?.displayPricePerMonth,
      displayPriceUnits: '/month',
      fullDisplayPrice: bundle?.displayPrice,
      label: isAnnual ? 'Prepay 1 year' : 'Prepay 4 months',
      strikeThroughDisplayPrice: bundle?.strikeThroughDisplayPricePerMonth,
      value: bundleFrequency,
    },
    {
      ...monthly,
      displayPrice: monthly?.displayPricePerMonth,
      displayPriceUnits: '/month',
      label: 'Pay monthly',
      value: billingFrequencySchema.enum.monthly,
    },
  ];
}

export function getPlanVisibility({
  hasStandalonePlan,
  semanticType,
  standalonePlanIsFetched,
}: {
  hasStandalonePlan: boolean;
  semanticType: string;
  standalonePlanIsFetched: boolean;
}) {
  const showStandalone =
    semanticType !== 'Course' && (!standalonePlanIsFetched || (standalonePlanIsFetched && hasStandalonePlan));
  return {
    showStandalone,
    showStandaloneLink: showStandalone,
    showTabs: showStandalone,
    showSubscriptionOnlyTitle: !showStandalone,
  };
}

export function shouldShowPromotionOverlay(
  promotion: Pick<NonNullable<SanitizedPromotion>, 'isActive' | 'type'> | null | undefined
): boolean {
  return Boolean(
    promotion?.isActive && (promotion.type === 'standard-promotion' || promotion.type === 'personalized-discount')
  );
}
